if (!customElements.get('lumino-category-slider')) {
  customElements.define('lumino-category-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lumino-category-slider__track');
      if (!this.track) return;

      this.abortController?.abort();
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.buttons = Array.from(this.querySelectorAll('[data-category-direction]'));
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateControls();
        });
      };

      this.track.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      this.track.addEventListener('keydown', event => this.onKeydown(event), options);
      this.querySelectorAll('[data-category-all]').forEach(anchor => {
        anchor.addEventListener('click', event => this.openCategories(event, anchor), options);
      });
      this.buttons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.getAttribute('aria-disabled') === 'true') return;
          this.move(button.dataset.categoryDirection === 'next' ? 1 : -1);
        }, options);
      });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const item = event.target.closest('.lumino-category-slider__item');
        if (item) this.reveal(item, 'auto');
      }, options);

      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.track);
      this.updateControls();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.clearDrawerListeners();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    get behavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    get rowCount() {
      return Math.max(1, getComputedStyle(this.track).gridTemplateRows.trim().split(/\s+/).length);
    }

    clearDrawerListeners() {
      this.drawerAbortController?.abort();
      this.drawerObserver?.disconnect();
      if (this.drawerCleanupTimer) clearTimeout(this.drawerCleanupTimer);
      this.drawerCleanupTimer = null;
    }

    openCategories(event, anchor) {
      if (event.defaultPrevented || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || anchor.target === '_blank') return;
      const drawer = document.getElementById('site-menu-sidebar');
      if (!drawer || typeof drawer.show !== 'function' || typeof window.inertElems?.forEach !== 'function') return;
      const closeButton = drawer.querySelector('[data-js-close]');
      if (!closeButton) return;

      event.preventDefault();
      this.clearDrawerListeners();
      const otherDrawer = document.querySelector('sidebar-drawer.sidebar--opened');
      if (otherDrawer && otherDrawer !== drawer && typeof otherDrawer.hide === 'function') otherDrawer.hide();
      this.drawerAbortController = new AbortController();
      const options = { signal: this.drawerAbortController.signal };
      drawer.addEventListener('keydown', keyboardEvent => {
        if (keyboardEvent.key !== 'Tab') return;
        const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter(element => element.getClientRects().length && !element.closest('[inert], [aria-hidden="true"]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          keyboardEvent.preventDefault();
          drawer.focus({ preventScroll: true });
        } else if (keyboardEvent.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
          keyboardEvent.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!keyboardEvent.shiftKey && (document.activeElement === last || document.activeElement === drawer)) {
          keyboardEvent.preventDefault();
          first.focus({ preventScroll: true });
        }
      }, options);
      this.drawerObserver = new MutationObserver(() => {
        if (drawer.opened) return;
        anchor.setAttribute('aria-expanded', 'false');
        if (drawer.contains(document.activeElement) && anchor.isConnected) anchor.focus({ preventScroll: true });
        this.clearDrawerListeners();
        this.drawerCleanupTimer = setTimeout(() => {
          if (window.lastFocusedElm === anchor) window.lastFocusedElm = null;
          this.drawerCleanupTimer = null;
        }, 150);
      });
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      window.lastFocusedElm = anchor;
      anchor.setAttribute('aria-expanded', 'true');
      drawer.show();
      closeButton.focus({ preventScroll: true });
    }

    updateControls() {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      this.dataset.overflow = maximum > 2 ? 'true' : 'false';
      this.buttons.forEach(button => {
        const disabled = button.dataset.categoryDirection === 'previous' ? position <= 2 : position >= maximum - 2;
        button.setAttribute('aria-disabled', String(disabled));
      });
    }

    move(direction) {
      const items = Array.from(this.track.querySelectorAll('.lumino-category-slider__item'));
      if (!items.length) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const trackRect = this.track.getBoundingClientRect();
      const position = Math.abs(this.track.scrollLeft);
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const starts = items.filter((item, index) => index % this.rowCount === 0).map(item => {
        const itemRect = item.getBoundingClientRect();
        return position + (rtl ? trackRect.right - itemRect.right : itemRect.left - trackRect.left);
      });
      const candidates = starts.filter(start => direction > 0 ? start > position + 1 : start < position - 1);
      const desired = position + this.track.clientWidth * direction;
      let destination = direction > 0 ? maximum : 0;
      if (candidates.length) {
        destination = direction > 0
          ? candidates.filter(start => start <= desired).at(-1) ?? candidates[0]
          : candidates.find(start => start >= desired) ?? candidates.at(-1);
      }
      destination = Math.max(0, Math.min(maximum, destination));
      this.track.scrollTo({ left: destination * (rtl ? -1 : 1), behavior: this.behavior });
    }

    reveal(item, behavior = this.behavior) {
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.left >= trackRect.left - 1 && itemRect.right <= trackRect.right + 1) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const distance = rtl ? itemRect.right - trackRect.right : itemRect.left - trackRect.left;
      if (distance) this.track.scrollBy({ left: distance, behavior });
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const direction = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
      const activeLink = event.target.closest('.lumino-category-slider__tile[href]');
      if (!activeLink) {
        this.move(direction);
        return;
      }

      const items = Array.from(this.track.querySelectorAll('.lumino-category-slider__item'));
      const index = items.indexOf(activeLink.closest('.lumino-category-slider__item'));
      let nextIndex = index + direction * this.rowCount;
      while (nextIndex >= 0 && nextIndex < items.length) {
        const nextLink = items[nextIndex].querySelector('.lumino-category-slider__tile[href]');
        if (nextLink) {
          nextLink.focus({ preventScroll: true });
          this.reveal(items[nextIndex]);
          return;
        }
        nextIndex += direction * this.rowCount;
      }
      this.move(direction);
    }
  });
}

